<template>
  <v-app>
    <v-main :class="containerState">
      <v-app-bar id="upp-bar" color="white" :flat="true" class="pl-4" border-sm>
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2" size="large">
            mdi-file-tree
          </v-icon>
          <a
            class="text-primary text-h5 text-decoration-none font-weight-light"
            href="/mashups"
          >
            {{ $t("labels.mashups") }}
          </a>
          <v-icon>mdi-chevron-right</v-icon>
          <v-tooltip bottom color="primary" class="hidden-lg-and-up">
            <template v-slot:activator="{ props }">
              <div
                id="mashup_title"
                class="text-primary text-h5 font-weight-bold text-truncate"
                v-bind="props"
              >
                {{ mashup.title }}
              </div>
            </template>
            <span>{{ mashup.title }}</span>
          </v-tooltip>
        </div>
        <!--Menu Version-->
        <div class="d-flex align-center">
          <v-menu v-if="versions">
            <template v-slot:activator="{ props }">
              <v-btn
                color="black"
                class="ml-4"
                id="version-button"
                width="auto"
                :max-width="windowWidth > 1165 ? 340 : 300"
                block
                variant="outlined"
                v-bind="props"
              >
                <v-tooltip color="primary">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      {{ versionName }}
                      <span v-if="isVersionReleased">
                        -
                        <span class="text-secondary">
                          {{ $t("labels.released") }}
                        </span>
                      </span>
                    </span>
                  </template>
                  <span>
                    {{ versionName }}
                    <span v-if="isVersionReleased">
                      - {{ $t("labels.released") }}</span
                    ></span
                  >
                </v-tooltip>
                <template v-slot:append>
                  <v-icon>mdi-triangle-small-down</v-icon>
                </template>
              </v-btn>
            </template>
            <v-card>
              <v-list>
                <v-list-item class="text-center">
                  <v-tooltip right>
                    <template v-slot:activator="{ props }">
                      <v-btn
                        variant="flat"
                        v-bind="props"
                        color="secondary"
                        class="text-white mx-auto"
                        @click="dialogCreateVersion = true"
                      >
                        <v-icon class="ml-2" small>
                          mdi-source-branch-plus
                        </v-icon>
                        {{ $t("labels.create-version") }}
                      </v-btn>
                    </template>
                    {{ $t("strings.create-version-mashup-tooltip") }}
                  </v-tooltip>
                </v-list-item>
                <v-list-subheader class="px-4 text-black text-overline">{{
                  $t("labels.mashup-versions")
                }}</v-list-subheader>
                <v-divider></v-divider>
                <v-list-item
                  class="pl-3"
                  :link="true"
                  v-for="item in versions"
                  :key="item"
                >
                  <v-list-item-title @click="selectVersionId(item._id)">
                    <v-icon small class="mr-2">mdi-source-branch</v-icon>
                    {{ item.name }}
                  </v-list-item-title>
                  <template v-slot:append>
                    <v-list-item-action>
                      <v-tooltip top>
                        <template v-slot:activator="{ props }">
                          <span
                            v-if="item.released"
                            v-bind="props"
                            class="font-weight-bold text-overline text-secondary"
                          >
                            {{ $t("labels.released").toLowerCase() }}
                          </span>
                        </template>
                        {{ $t("strings.release-version-tooltip") }}
                      </v-tooltip>
                      <v-icon
                        v-if="!item.released && versions.length > 1"
                        color="secondary"
                        @click="openDialogDeleteVersion(item)"
                        >mdi-delete</v-icon
                      >
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
          <!--Release Button-->
          <v-tooltip>
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="text-white ml-2"
                variant="flat"
                color="secondary"
                @click="releaseVersion()"
                :disabled="isVersionReleased"
              >
                <v-icon left class="mr-2">mdi-tag</v-icon>
                {{ $t("labels.release") }}
              </v-btn>
            </template>
            <span>{{ $t("strings.release-version-button-tooltip") }}</span>
          </v-tooltip>
        </div>
        <template v-slot:append>
          <v-responsive>
            <v-btn-group
              class="hidden-md-and-down"
              color="primary"
              variant="text"
              slim
              density="compact"
            >
              <!--Configure button-->
              <v-btn @click="toggleSidebar('drawerConfig')">
                <v-icon center>mdi-cog-outline</v-icon>
                <span v-if="windowWidth >= 1366" class="ml-2">
                  {{ $t("labels.configure") }}
                </span>
                <v-tooltip
                  activator="parent"
                  location="bottom"
                  :disabled="windowWidth > 1366"
                >
                  {{ $t("labels.configure") }}
                </v-tooltip>
              </v-btn>
              <!--Flows button-->
              <v-btn @click="toggleSidebar('drawerFlow')">
                <v-icon center>mdi-transit-connection-horizontal</v-icon>
                <span v-if="windowWidth >= 1366" class="ml-2">
                  {{ $t("labels.flow") }}
                </span>
                <v-tooltip
                  activator="parent"
                  location="bottom"
                  :disabled="windowWidth > 1366"
                >
                  {{ $t("labels.flow") }}
                </v-tooltip>
              </v-btn>
              <!--Share button-->
              <v-btn @click.stop="toggleSidebar('drawerShare')">
                <v-icon center>
                  {{
                    mashup.owner === loggedUserId
                      ? "mdi-account-plus-outline"
                      : "mdi-account-eye-outline"
                  }}
                </v-icon>
                <span v-if="windowWidth >= 1366" class="ml-2">
                  {{ $t("labels.share") }}
                </span>
                <v-tooltip
                  activator="parent"
                  location="bottom"
                  :disabled="windowWidth > 1366"
                >
                  {{ $t("labels.share") }}
                </v-tooltip>
              </v-btn>
            </v-btn-group>
            <!--Dots Menu-->
            <v-menu :close-on-content-click="true">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-dots-vertical"
                  flat
                  density="comfortable"
                >
                </v-btn>
              </template>
              <v-list>
                <div class="hidden-lg-and-up">
                  <v-list-item
                    class="hidden-lg-and-up"
                    @click.stop="toggleSidebar('drawerConfig')"
                    :disabled="sidebarState.drawerConfig"
                  >
                    <v-list-item-title>
                      <v-icon color="primary">mdi-cog-outline</v-icon>
                      <span class="ml-5">{{ $t("labels.configure") }}</span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click.stop="toggleSidebar('drawerFlow')"
                    :disabled="sidebarState.drawerFlow"
                  >
                    <v-list-item-title
                      ><v-icon color="primary"
                        >mdi-transit-connection-horizontal</v-icon
                      >
                      <span class="ml-5">{{ $t("labels.flow") }}</span>
                    </v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    @click.stop="toggleSidebar('drawerShare')"
                    :disabled="sidebarState.drawerShare"
                  >
                    <v-list-item-title>
                      <v-icon color="primary">
                        {{
                          mashup.owner === loggedUserId
                            ? "mdi-account-plus-outline"
                            : "mdi-account-eye-outline"
                        }}
                      </v-icon>
                      <span class="ml-5">{{ $t("labels.share") }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-divider></v-divider>
                </div>
                <!-- DUPLICATE Option -->
                <v-tooltip
                  left
                  :disabled="
                    mashup.owner === loggedUserId || windowWidth <= 1365
                  "
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        :disabled="mashup.owner !== loggedUserId"
                        @click.stop="selectMashupVersion('duplicate')"
                      >
                        <v-list-item-title>
                          <v-icon color="primary" class="opacity-100"
                            >mdi-content-duplicate</v-icon
                          >
                          <span class="ml-5">
                            {{ $t("labels.duplicate") }}
                          </span>
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  {{ $t("strings.duplicate-mashup-tooltip") }}
                </v-tooltip>
                <!-- EXPORT Option -->
                <v-tooltip
                  left
                  :disabled="
                    mashup.owner === loggedUserId || windowWidth <= 1365
                  "
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        :disabled="mashup.owner !== loggedUserId"
                        @click.stop="selectMashupVersion('export')"
                      >
                        <v-list-item-title>
                          <v-icon color="primary" class="opacity-100"
                            >mdi-download
                          </v-icon>
                          <span class="ml-5">{{ $t("labels.export") }}</span>
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  {{ $t("strings.export-mashup-tooltip") }}
                </v-tooltip>
                <!-- DELETE Option -->
                <v-tooltip
                  left
                  :disabled="
                    mashup.owner === loggedUserId || windowWidth <= 1365
                  "
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        @click.stop="deleteMashup()"
                        :disabled="mashup.owner !== loggedUserId"
                      >
                        <v-list-item-title>
                          <v-icon class="opacity-100" color="primary"
                            >mdi-delete
                          </v-icon>
                          <span class="ml-5">{{ $t("labels.delete") }}</span>
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  {{ $t("strings.delete-mashup-tooltip") }}
                </v-tooltip>
              </v-list>
            </v-menu>
          </v-responsive>
        </template>
      </v-app-bar>
      <v-divider></v-divider>
      <!--DRAWERS-->
      <div :class="containerState">
        <SidebarConfigure
          :drawer-config="sidebarState.drawerConfig"
          @update:drawerConfig="toggleSidebar"
          :mfConfig="mfConfig"
          :realm="realm"
          :mashup-prop="mashup"
        />

        <SidebarFlow
          :drawer-flow="sidebarState.drawerFlow"
          @update:drawerFlow="toggleSidebar"
          :mfConfig="mfConfig"
          :realm="realm"
        />
        <SidebarShare
          :drawer-share="sidebarState.drawerShare"
          @update:drawerShare="toggleSidebar"
          :api-url="mfConfig?.api"
          :palette="palette"
          :realm="realm"
          :root="'projects'"
          v-model:mashup="mashup"
        />
        <!--Snackbar-->
        <v-snackbar
          v-model="snackbar"
          location="top"
          :color="snackbarColor"
          timeout="5000"
        >
          {{ snackbarText }}
          <template v-slot:actions="{ props }">
            <v-btn
              color="white"
              variant="text"
              v-bind="props"
              @click="redirect"
            >
              OK</v-btn
            >
          </template>
        </v-snackbar>
        <!--Alert-->
        <v-overlay z-index="100" v-if="showOverlay" text>
          <div class="ma-5 text-center">
            <img
              src="https://storage.digital-enabler.eng.it/imgs/commons/device-rotate.gif"
              width="200"
              alt="rotate-device"
            />
            <div class="ma-5">
              {{ $t("strings.overlay-text") }}
            </div>
            <v-btn color="info" @click="showOverlay = false"> OK </v-btn>
          </div>
        </v-overlay>
        <!--Dialogs-->
        <!-- Dialog Delete Mashup -->
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.delete") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeDeleteMashup">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <p class="text-h6">
                <span v-if="mashup.sharedWith?.length === 0">
                  {{ $t("strings.delete-mashup-question") }}
                </span>
                <span v-else>
                  {{ $t("strings.delete-mashup-shared-question") }}
                </span>
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeDeleteMashup"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="deleteMashupConfirm"
              >
                {{ $t("labels.delete") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- Dialog Delete Version-->
        <v-dialog v-model="dialogVersionDelete" max-width="500px">
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.delete") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeDeleteVersion">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <p class="text-h6">
                {{ $t("strings.delete-version-question") }}
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeDeleteVersion"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="deleteVersionConfirm"
              >
                {{ $t("labels.delete") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!--Dialog on create new Release-->
        <v-dialog v-model="dialogRelease" max-width="500px">
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.release") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeReleaseVersion">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <p class="text-h6">
                {{ $t("strings.release-version") }} {{ this.versionName }}?
              </p>
              <p class="my-2">
                {{ $t("strings.release-version-text") }}<br /><br />
                <v-icon color="secondary">mdi-alert</v-icon>
                <span
                  v-html="$t('strings.release-version-text-warning')"
                ></span>
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeReleaseVersion"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="releaseVersionConfirm"
              >
                {{ $t("labels.release") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!--Dialog Create new version-->
        <v-dialog v-model="dialogCreateVersion" max-width="500px" persistent>
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("strings.create-new-version") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeCreateVersion">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="createVersionId"
                    :label="$t('labels.from')"
                    variant="outlined"
                    :placeholder="latestVersion.name"
                    item-title="name"
                    item-value="_id"
                    density="compact"
                    :items="versions"
                    :hint="$t('strings.create-version-from-hint')"
                    :persistent-hint="true"
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item v-bind="props">
                        <template v-slot:append>
                          <v-chip
                            v-if="
                              item.title === versions[versions.length - 1].name
                            "
                            text-color="white"
                            color="secondary"
                            small
                            >default
                          </v-chip>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-form ref="createForm" v-model="validName" lazy-validation @submit.prevent="createVersion(editedVersionName)">
                    <v-text-field
                      v-model="editedVersionName"
                      :label="$t('labels.name')"
                      :hint="$t('strings.create-version-hint')"
                      :persistent-hint="true"
                      variant="outlined"
                      :rules="nameRules"
                      required
                      density="compact"
                    ></v-text-field>
                  </v-form>
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeCreateVersion"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                :disabled="!validName"
                variant="flat"
                color="secondary"
                class="text-white"
                @click="createVersion(editedVersionName)"
              >
                {{ $t("labels.create") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!--Dialog Duplicate & Export-->
        <v-dialog
          v-model="dialogSelectVersion"
          width="auto"
          max-width="500px"
          :persistent="true"
        >
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("labels.duplicate") }}
                </span>
                <span v-else>
                  {{ $t("labels.export") }}
                </span>
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon @click="closeSelectVersion">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>

            <v-card-text>
              <p class="text-h6 mb-4">
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("strings.duplicate-mashup-question") }}
                </span>
                <span v-else>
                  {{ $t("strings.export-mashup-question") }}
                </span>
              </p>
              <v-form ref="createForm" v-model="validName" lazy-validation>
                <v-row class="d-flex justify-left">
                  <v-col cols="12" sm="10" class="mb-2">
                    <v-select
                      v-model="selectedVersion"
                      :items="versions"
                      item-title="name"
                      item-value="_id"
                      :label="$t('labels.mashup-versions')"
                      density="compact"
                      variant="outlined"
                      hide-details="auto"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" v-if="actionSelectVersion === 'duplicate'">
                    <v-text-field
                      v-model="editedItem.title"
                      :label="$t('labels.name')"
                      :hint="$t('strings.mashup-project-name')"
                      :persistent-hint="true"
                      variant="outlined"
                      :rules="nameRules"
                      required
                      density="compact"
                      name="name"
                      :clearable="true"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeSelectVersion"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="versionMashupConfirm"
              >
                <span v-if="actionSelectVersion === 'duplicate'">
                  {{ $t("labels.duplicate") }}
                </span>
                <span v-else>
                  {{ $t("labels.export") }}
                </span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-main>
  </v-app>
</template>

<script>
// import { nextTick } from "vue";

import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";
import MashupMixin from "@/mixins/AppMixin.js";
import SidebarConfigure from "@/components/SidebarConfigure.vue";
import SidebarFlow from "@/components/SidebarFlow.vue";
import SidebarShare from "@/components/SidebarShare.vue";

export default {
  name: "EditorAppBar",
  components: { SidebarConfigure, SidebarFlow, SidebarShare },
  mixins: [events, utils, MashupMixin],
  props: ["mfConfig", "realm", "palette"],
  data() {
    return {
      containerState: "expanded",
      version: {},
      snackbar: false,
      snackbarRedirect: false,
      snackbarText: "",
      snackbarColor: "blue",
      loggedUserId: "",
      dialogDelete: false,
      dialogVersionDelete: false,
      validName: true,
      isVersionReleased: false,
      nameRules: [
        (value) => !!value || this.$t("errors.version-name-required"),
      ],
      dialogRelease: false,
      dialogDuplicate: false,
      dialogCreateVersion: false,
      windowWidth: window.innerWidth,
      orientationMessage: false,
      showOverlay: false,
      initialOrientation: "",
      editedVersionName: "",
      editedItem: {
        _id: "",
        title: "",
        owner: "",
        tags: [],
        latestRelease: "",
        visibility: "",
        sharedWith: [],
        versions: [],
      },
      sidebarState: {
        drawerConfig: false,
        drawerFlow: false,
        drawerShare: false,
      },
    };
  },
  methods: {
    openDialogDeleteVersion(version) {
      this.dialogVersionDelete = true;
      this.version = version;
    },
    async selectVersionId(event) {
      this.currentVersion = this.findVersion(event);
      this.versionName = this.currentVersion.name;
      this.isVersionReleased = this.currentVersion.released;
      await this.$router.push({
        path:
          `/` + this.realm + `/draw/` + this.mashupId + `/v/` + event + `/edit`,
      });
      this.reloadEditorContent();
    },
    reloadEditorContent() {
      document.dispatchEvent(new CustomEvent("refresh-mashup-version"));
    },
    selectCreateVersionId(event) {
      this.createVersionId = event;
    },
    redirect() {
      this.snackbar = false;
      if (this.snackbarRedirect) {
        this.$router.push("/mashups");
      }
    },
    handleOrientationChange() {
      const { orientation } = window;
      if (
        orientation === 0 &&
        !this.initialOrientation &&
        window.outerWidth >= 768
      ) {
        this.initialOrientation = "portrait";
        this.showOverlay = true;
      } else {
        this.showOverlay = false;
      }
    },

    // Funzione per chiudere tutte le altre sidebar tranne quella specificata
    closeOtherSidebars(currentSidebar) {
      for (const sidebar in this.sidebarState) {
        if (sidebar !== currentSidebar) {
          this.sidebarState[sidebar] = false;
        }
      }
    },
    // Funzione per gestire l'apertura/chiusura di una sidebar
    toggleSidebar(sidebar) {
      this.closeOtherSidebars(sidebar);
      this.sidebarState[sidebar] = !this.sidebarState[sidebar];
    },
  },
  created() {
    this.handleOrientationChange();
  },
  beforeUnmount() {
    window.removeEventListener(
      "orientationchange",
      this.handleOrientationChange,
    );
  },
  mounted() {
    this.loggedUserId = localStorage.getItem("loggedUserId");
    document.addEventListener("language-localstorage-changed", (event) => {
      this.setLocale(event.detail.storage);
    });
    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });

    document.addEventListener("refresh-mashup", () => {
      this.getMashup();
    });

    document.addEventListener("confirmation-dialog", () => {
      this.snackbarColor = "green";
      this.snackbarText = this.$t("labels.update-mashup-snackbar-text");
      this.snackbar = true;
    });

    document.addEventListener("duplicate-snackbar", (event) => {
      if (event.detail.storage) {
        this.snackbarColor = "green";
        this.snackbarText = this.$t("strings.duplicate-mashup-text");
        this.snackbar = event.detail.storage;
      }
    });
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
};
</script>

<style scoped>
#mashup_title {
  width: auto;
  max-width: 250px;
}

#version-button {
  font-size: 12px;
}

#version-button :deep(.v-btn__content) {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
}

.v-list-item__title {
  cursor: pointer;
}

main.expanded {
  margin-left: 56px;
}

.side-buttons.expanded {
  margin-right: 56px;
}

main.expanded header#upp-bar {
  margin-left: 56px;
  width: calc(100% - 56px) !important;
}

main.reduced {
  margin-left: 256px;
}

.side-buttons.reduced {
  margin-right: 256px;
}

main.reduced header#upp-bar {
  margin-left: 256px;
  width: calc(100% - 256px) !important;
}
</style>
